import { KBDocument, KBChunk, CitedSource } from '../types';
import { db } from './db';
import { embedText, cosineSimilarity } from './embeddings';

const MAX_CHUNK_CHARS = 1200; // ~200-400 tokens
const MIN_SIMILARITY = 0.3;

function splitIntoChunks(text: string): string[] {
  const paragraphs = text
    .split(/\n\s*\n/)
    .map(p => p.trim())
    .filter(Boolean);

  const chunks: string[] = [];
  let current = '';

  for (const paragraph of paragraphs) {
    const candidate = current ? `${current}\n\n${paragraph}` : paragraph;

    if (candidate.length <= MAX_CHUNK_CHARS) {
      current = candidate;
      continue;
    }

    if (current) {
      chunks.push(current);
    }

    // A single paragraph longer than the target size still gets its own chunk as-is
    // (sentence-level splitting isn't worth the complexity at this scale).
    current = paragraph;
  }

  if (current) {
    chunks.push(current);
  }

  return chunks;
}

export class KnowledgeBaseService {
  static async ingestDocument(doc: KBDocument): Promise<void> {
    const existing = await db.kbChunks.where('docId').equals(doc.id).toArray();
    if (existing.length > 0) return; // already ingested, idempotent

    await db.kbDocs.put(doc);

    const chunkTexts = splitIntoChunks(doc.text);
    const chunks: KBChunk[] = [];

    for (let i = 0; i < chunkTexts.length; i++) {
      const embedding = await embedText(chunkTexts[i]);
      chunks.push({
        id: `${doc.id}#${i}`,
        docId: doc.id,
        docTitle: doc.title,
        source: doc.source,
        url: doc.url,
        placeholder: doc.placeholder,
        text: chunkTexts[i],
        embedding
      });
    }

    if (chunks.length > 0) {
      await db.kbChunks.bulkPut(chunks);
    }
  }

  static async seedIfEmpty(documents: KBDocument[]): Promise<void> {
    const count = await db.kbDocs.count();
    if (count > 0) return;
    for (const doc of documents) {
      await this.ingestDocument(doc);
    }
  }

  static async retrieveRelevant(query: string, topK = 3): Promise<CitedSource[]> {
    const allChunks = await db.kbChunks.toArray();
    if (allChunks.length === 0) return [];

    const queryEmbedding = await embedText(query);

    return allChunks
      .map(chunk => ({
        chunk,
        similarity: cosineSimilarity(queryEmbedding, chunk.embedding)
      }))
      .filter(({ similarity }) => similarity >= MIN_SIMILARITY)
      .sort((a, b) => b.similarity - a.similarity)
      .slice(0, topK)
      .map(({ chunk, similarity }) => ({
        docTitle: chunk.docTitle,
        source: chunk.source,
        url: chunk.url,
        placeholder: chunk.placeholder,
        excerpt: chunk.text,
        similarity: Math.round(similarity * 100) / 100
      }));
  }
}
